import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { Search } from "lucide-react";
import { dictionary } from "../../../utils/dictionary";
import { useAuth } from "../../../context/AuthContext";

interface SearchPreviousProductsProps {
  previousProducts: any[];
  isPreviousProductsLoading: boolean;
  handlePreviousProductSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function SearchPreviousProducts({
  previousProducts,
  isPreviousProductsLoading,
  handlePreviousProductSelect,
}: SearchPreviousProductsProps) {
  const { language } = useAuth();
  
  const safeProducts = Array.isArray(previousProducts) ? previousProducts : [];
  
  const getNoOptionsText = () => {
    if (isPreviousProductsLoading) {
      return dictionary["loading-products"]?.[language];
    }
    
    if (safeProducts.length === 0) {
      return dictionary["no-products-found"]?.[language];
    }
    
    return dictionary["no-products-found-onboarding"]?.[language];
  };
  
  const getPlaceholder = () => {
    if (isPreviousProductsLoading) {
      return dictionary["loading-products"]?.[language];
    }
    
    return dictionary["search-previous-products-placeholder"]?.[language];
  };
  
  return (
    <div className="flex-1 relative">
      <Autocomplete
        id="previous-products-autocomplete"
        options={safeProducts}
        loading={isPreviousProductsLoading}
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, product) => {
          if (product) handlePreviousProductSelect({ target: { value: product.id } } as any);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={getPlaceholder()}
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Search className="w-5 h-5 text-gray-500 mr-2" />
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {isPreviousProductsLoading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
              className: "border border-gray-100 rounded-md bg-white",
              style: {
                color: 'black',
                WebkitTextFillColor: 'black', // For webkit browsers
              },
            }}
          />
        )}
        renderOption={(props: React.HTMLAttributes<HTMLLIElement> & { 'data-option-index'?: number }, option) => (
          <li {...props} key={option.id || `product-${props['data-option-index']}`} className="px-4 py-2 hover:bg-gray-100 cursor-pointer">
            <div className="flex items-center">
              {option.images && option.images[0] && (
                <img
                  src={option.images[0]}
                  alt={option.name}
                  className="w-10 h-10 object-cover rounded-md mr-3"
                />
              )}
              <div>
                <div className="font-medium">{option.name || 'Unnamed Product'}</div>
                <div className="text-sm text-gray-500">${option.price || '0.00'}</div>
              </div>
            </div>
          </li>
        )}
        noOptionsText={getNoOptionsText()}
        loadingText={dictionary["loading-products"]?.[language]}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: '48px',
            borderRadius: '16px',
            padding: '0 14px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#8284F3',
              borderWidth: '1px',
            },
          },
          '& .MuiAutocomplete-endAdornment': {
            right: '8px',
          },
          '& .MuiAutocomplete-inputRoot': {
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiAutocomplete-input': {
            height: '40px',
            padding: '0 !important',
            color: 'black',
          }
        }}
      />
    </div>
  );
}

export default SearchPreviousProducts;
